import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { CostSummaryCard } from './CostSummaryCard';
import { CostListTable } from './CostListTable';
import { CostEntryForm } from './CostEntryForm';
import { useJobCosts } from '../hooks/useJobCosts';
import { useJobCostSummary } from '../hooks/useJobCostSummary';
import { useDeleteJobCost } from '../hooks/useDeleteJobCost';
import type { JobCost } from '../types/jobCost';

interface JobCostsTabProps {
  jobId: number;
}

export function JobCostsTab({ jobId }: JobCostsTabProps) {
  const [isForecast, setIsForecast] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<JobCost | null>(null);

  const {
    data: costs,
    isLoading: isLoadingCosts,
    error: costsError,
    refetch: refetchCosts,
  } = useJobCosts(jobId, isForecast);

  const {
    data: summary,
    isLoading: isLoadingSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useJobCostSummary(jobId, isForecast);

  const { deleteCost, isDeleting } = useDeleteJobCost();

  const handleAddCost = () => {
    setEditingCost(null);
    setIsFormOpen(true);
  };

  const handleEditCost = (cost: JobCost) => {
    setEditingCost(cost);
    setIsFormOpen(true);
  };

  const handleDeleteCost = async (costId: number) => {
    const success = await deleteCost(jobId, costId);
    if (success) {
      refetchCosts();
      refetchSummary();
    }
  };

  const handleFormSuccess = () => {
    refetchCosts();
    refetchSummary();
  };

  const isLoading = isLoadingCosts || isLoadingSummary;
  const error = costsError || summaryError;

  return (
    <div className="p-4 space-y-4">
      {/* Header with toggle and add button */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={!isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(false)}
            className={
              !isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Actual
          </Button>
          <Button
            variant={isForecast ? 'default' : 'outline'}
            onClick={() => setIsForecast(true)}
            className={
              isForecast
                ? 'bg-[#0A0A9E] text-white hover:bg-[#0A0A9E]/90'
                : 'text-gray-600 hover:bg-gray-100'
            }
          >
            Forecast
          </Button>
        </div>

        <Button variant="primary" onClick={handleAddCost}>
          Add Cost
        </Button>
      </div>

      <ErrorAlert error={error} />

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" label="Loading costs..." />
        </div>
      ) : (
        <>
          {/* Summary card */}
          {summary && <CostSummaryCard summary={summary} />}

          {/* Costs table */}
          <CostListTable
            jobId={jobId}
            costs={costs}
            onEdit={handleEditCost}
            onDelete={handleDeleteCost}
            isDeleting={isDeleting}
          />
        </>
      )}

      {/* Cost entry form dialog */}
      <CostEntryForm
        jobId={jobId}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCost(null);
        }}
        onSuccess={handleFormSuccess}
        editingCost={editingCost}
        isForecast={isForecast}
      />
    </div>
  );
}
